// `{{var}}` placeholders in flow text, URLs and headers.

import type { FlowValue, FlowVars } from './types';

/** Letters, digits and `_`, in dot-separated parts: `answer`, `contact.email`. */
export const VARIABLE_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;

const PLACEHOLDER_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_.]*)\s*\}\}/g;

function toText(v: FlowValue | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

/** Replace each `{{name}}` with its value; an unset variable becomes ''.
 *  `encode` is applied to each value (e.g. encodeURIComponent inside a URL),
 *  never to the surrounding template. */
export function renderTemplate(template: string, vars: FlowVars, encode?: (s: string) => string): string {
  return template.replace(PLACEHOLDER_RE, (_, name: string) => {
    const text = toText(vars[name]);
    return encode ? encode(text) : text;
  });
}

/** Variable names a template refers to. */
export function templateVariables(template: string): string[] {
  return [...template.matchAll(PLACEHOLDER_RE)].map((m) => m[1]!);
}

/** Read a dotted path (`order.items.0.name`) out of a parsed JSON value.
 *  Objects and arrays come back as JSON text; a missing path is undefined. */
export function readPath(value: unknown, path: string): FlowValue | undefined {
  let cur: unknown = value;
  for (const part of path.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  if (cur === undefined) return undefined;
  if (cur === null || typeof cur === 'string' || typeof cur === 'number' || typeof cur === 'boolean') return cur;
  return JSON.stringify(cur);
}
